/**
 * Composite pieces: the pricing-runs detail (read-only) and the row action menu.
 * The generic multi-select filter popover now lives in the shared kit
 * (LwFilterMenu) and is re-exported here as FilterMenu for existing importers.
 */
import React, { useEffect, useRef, useState } from 'react';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import { Eye, Mail, MailCheck, ArrowRight, Pencil, Ban, RotateCcw, AlertTriangle } from 'lucide-react';
import { money, moneyFull } from './bits';
import { LW_DS_CLASS } from '@/components/lw/LwThemeScope';
import type { LoProspect, LoProspectPricingFailure, PricingScenario } from '@/types/lo-prospect';

export { LwFilterMenu as FilterMenu, type FilterOption } from '@/components/lw';

function compTargetLabel(compPercent: number): string {
  return compPercent === 0 ? 'Par' : `${compPercent.toFixed(2)}% comp`;
}

/**
 * Which Pricing Profile produced a run. Profile-backed runs show the snapshotted
 * label (covers assigned_missing_used_default too — the default's label, with a
 * tooltip noting the assigned profile couldn't be resolved). Env-priced runs get
 * a reason-derived fallback so batches are never ambiguous; legacy pre-migration
 * rows (no reason) render nothing.
 */
function pricingProfileChip(scn: PricingScenario): { text: string; title?: string } | null {
  if (scn.pricingProfileLabel) {
    return scn.pricingProfileResolutionReason === 'assigned_missing_used_default'
      ? { text: scn.pricingProfileLabel, title: 'The assigned pricing profile could not be resolved — the default profile priced this run.' }
      : { text: scn.pricingProfileLabel };
  }
  if (scn.pricingProfileResolutionReason === 'flag_off') {
    return { text: 'Environment pricing — flag off' };
  }
  if (scn.pricingProfileResolutionReason) {
    return { text: 'Environment fallback — profile configuration unavailable' };
  }
  return null;
}

function ScnCard({ scn, hist = false }: { scn: PricingScenario; hist?: boolean }) {
  // Best badge goes on the first qualified row only (results sort qualified-first).
  const firstQualified = scn.results.findIndex((r) => r.qualified);
  const showCompNote = scn.compPercent !== null && !scn.compFunded && scn.results.length > 0;
  const profileChip = pricingProfileChip(scn);
  return (
    <div className={`scn-card${hist ? ' histcard' : ''}`}>
      <div className="scn-head">
        <div className="scn-name">
          {scn.type} · {money(scn.amount)}
          {scn.compPercent !== null && <span className="run-when">{compTargetLabel(scn.compPercent)} target</span>}
          {scn.ranAt && <span className="run-when">priced {new Date(scn.ranAt).toLocaleDateString()}</span>}
          {profileChip && (
            <span className="run-when" title={profileChip.title}>
              {profileChip.text}
            </span>
          )}
        </div>
        <div className="scn-tags">
          {scn.product && <span className="pill-prod">{scn.product}</span>}
          {scn.fico !== null && <span>{scn.fico} FICO</span>}
          {scn.ltv !== null && (
            <>
              <span className="dotsep" />
              <span>{scn.ltv}% LTV</span>
            </>
          )}
          {scn.term !== null && (
            <>
              <span className="dotsep" />
              <span>{scn.term}yr</span>
            </>
          )}
          {scn.lock !== null && (
            <>
              <span className="dotsep" />
              <span>{scn.lock}-day lock</span>
            </>
          )}
        </div>
      </div>
      {showCompNote && (
        <div className="comp-note">
          No investor funds your {compTargetLabel(scn.compPercent as number)} at this scenario — closest options shown below.
        </div>
      )}
      <div className="res-row head">
        <span>Investor</span>
        <span>Rate</span>
        <span>Price</span>
        <span>Monthly P&amp;I</span>
        <span>Your make</span>
      </div>
      {scn.results.length === 0 && (
        <div className="res-row">
          <div className="res-inv" style={{ gridColumn: '1 / -1', color: 'var(--lw-muted)' }}>
            No eligible products returned for this scenario.
          </div>
        </div>
      )}
      {scn.results.map((r, j) => (
        <div
          className={`res-row${j === firstQualified ? ' best' : ''}${!r.qualified ? ' below-target' : ''}`}
          key={`${r.investor}|${r.productName}|${r.rate}|${r.price}`}
        >
          <div className="res-inv">
            <span className="res-inv-name">{r.displayInvestor || r.investor}</span>
            {j === firstQualified && <span className="best-tag">Best</span>}
            <span className="res-prod">
              {r.displayProductName || '—'}
              {r.closingCost ? ` · ${moneyFull(r.closingCost)} cost` : ''}
            </span>
          </div>
          <div className="res-val res-rate">{r.rate.toFixed(3)}%</div>
          <div className="res-val">{r.price.toFixed(3)}</div>
          <div className="res-val">{moneyFull(r.payment)}/mo</div>
          <div className="res-val">
            {r.makeDollars ? moneyFull(r.makeDollars) : '—'}
            <span className="res-sub"> {r.makePercent.toFixed(2)}%</span>
          </div>
        </div>
      ))}
    </div>
  );
}

const FAILURE_CATEGORY_LABEL: Record<LoProspectPricingFailure['category'], string> = {
  scenario_validation: 'Scenario input',
  upstream_auth: 'Pricing connection',
  upstream_rate_limit: 'Rate limited',
  timeout: 'Timed out',
  network: 'Network',
  upstream_unavailable: 'Optimal Blue unavailable',
  invalid_response: 'Unexpected response',
  legacy: 'Legacy record'
};

const SCENARIO_LABELS: Record<string, string> = {
  loanPurpose: 'Purpose',
  loanType: 'Loan type',
  productTypes: 'Product filters',
  loanAmount: 'Loan amount',
  appraisedValue: 'Appraised value',
  purchasePrice: 'Purchase price',
  fico: 'FICO',
  dti: 'DTI',
  dscrRatio: 'DSCR',
  termYears: 'Term',
  lockDays: 'Lock',
  compPercent: 'Comp target',
  targetRate: 'Target rate',
  propertyState: 'State',
  propertyZip: 'ZIP',
  propertyCounty: 'County',
  propertyType: 'Property type',
  occupancy: 'Occupancy',
  lienType: 'Lien position',
  firstLienAmount: 'First lien',
  amortizationType: 'Amortization',
  armFixedTerm: 'ARM fixed term',
  veteranType: 'Veteran type',
  incomeVerificationType: 'Income documentation'
};

function failureValue(key: string, value: unknown): string {
  if (Array.isArray(value)) {
    return value.map(String).join(', ');
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  if (typeof value === 'number' && ['loanAmount', 'appraisedValue', 'purchasePrice', 'firstLienAmount'].includes(key)) {
    return moneyFull(value);
  }
  if (typeof value === 'number' && ['dti', 'compPercent', 'targetRate'].includes(key)) {
    if (key === 'compPercent' && value === 0) {
      return 'Par';
    }
    return `${value}%`;
  }
  if (key === 'termYears' && typeof value === 'number') {
    return `${value} years`;
  }
  if (key === 'lockDays' && typeof value === 'number') {
    return `${value} days`;
  }
  return String(value ?? '—');
}

function FailureCard({ failure }: { failure: LoProspectPricingFailure }) {
  const scenarioEntries = Object.entries(failure.scenario)
    .filter(([key, value]) => key in SCENARIO_LABELS && value !== null && value !== '' && value !== undefined)
    .slice(0, 24);

  return (
    <article className="pricing-failure-card">
      <div className="pricing-failure-head">
        <span className="pricing-failure-icon"><AlertTriangle aria-hidden="true" /></span>
        <div className="pricing-failure-title">
          <strong>Pricing attempt rejected</strong>
          <span>{new Date(failure.attemptedAt).toLocaleString()}</span>
        </div>
        <div className="pricing-failure-tags">
          <span>{FAILURE_CATEGORY_LABEL[failure.category]}</span>
          {failure.httpStatus !== null && <span>HTTP {failure.httpStatus}</span>}
          <span className="pricing-failure-zero">0 runs used</span>
        </div>
      </div>
      <div className="pricing-failure-body">
        <p>{failure.message}</p>
        {failure.issues.length > 0 && (
          <ul className="pricing-failure-issues">
            {failure.issues.map((issue, index) => (
              <li key={`${issue.input ?? 'general'}-${index}`}>
                {issue.input && <strong>{SCENARIO_LABELS[issue.input] ?? issue.input}: </strong>}
                {issue.message}
              </li>
            ))}
          </ul>
        )}
        {failure.legacy && (
          <div className="pricing-failure-legacy">
            This attempt predates detailed diagnostics, so its submitted values are unavailable.
          </div>
        )}
        {scenarioEntries.length > 0 && (
          <details className="pricing-failure-details">
            <summary>Submitted scenario · {scenarioEntries.length} recorded fields</summary>
            <dl>
              {scenarioEntries.map(([key, value]) => (
                <div key={key}>
                  <dt>{SCENARIO_LABELS[key]}</dt>
                  <dd>{failureValue(key, value)}</dd>
                </div>
              ))}
            </dl>
          </details>
        )}
        <div className="pricing-failure-ref">Support reference · {failure.id}</div>
      </div>
    </article>
  );
}

export function ScenarioDetail({
  prospect,
  onReset,
  onRunScenario,
  busy,
  failures = [],
  failuresLoading = false,
  failuresError = '',
  allowInRecruitingReset = false,
  allowInRecruitingPricing = false
}: {
  prospect: LoProspect;
  onReset?: () => void;
  onRunScenario?: () => void;
  busy?: boolean;
  failures?: LoProspectPricingFailure[];
  failuresLoading?: boolean;
  failuresError?: string;
  allowInRecruitingReset?: boolean;
  allowInRecruitingPricing?: boolean;
}) {
  const hasHistory = prospect.history.length > 0;
  // A ready managed-user seat is the only post-transfer pricing exception.
  // The edge function and capture RPC independently revalidate it.
  const pricingTerminal = prospect.stage === 'passed'
    || (prospect.stage === 'in_recruiting' && !allowInRecruitingPricing);
  const canReset = prospect.stage !== 'passed'
    && (prospect.stage !== 'in_recruiting' || allowInRecruitingReset);
  return (
    <div className="detail-inner">
      <div className="detail-head">
        <div className="detail-title">
          {prospect.scenarios.length > 0
            ? `${prospect.scenarios.length} scenario${prospect.scenarios.length > 1 ? 's' : ''} run · investor pricing returned`
            : '5 fresh runs available · awaiting new scenarios'}
        </div>
        <div className="detail-actions">
          {onRunScenario && !pricingTerminal && prospect.runsUsed < 5 && (
            <button className="btn-sm solid" onClick={onRunScenario} disabled={busy}>
              {busy ? 'Pricing…' : 'Run a scenario'}
            </button>
          )}
          {onReset && canReset && prospect.runsUsed > 0 && (
            <button className="btn-sm" onClick={onReset} disabled={busy}>
              <RotateCcw size={15} /> Reset runs
            </button>
          )}
        </div>
      </div>

      {prospect.scenarios.length > 0 ? (
        prospect.scenarios.map((s) => <ScnCard key={s.id} scn={s} />)
      ) : (
        <div className="fresh-note">
          <span className="badge badge--previewing">
            <span className="bdot" />0 of 5 used
          </span>
          {hasHistory ? (
            <span>Runs were reset — {prospect.firstName} has a clean batch of 5. Previous rounds are saved below.</span>
          ) : (
            <span>{prospect.firstName || 'This prospect'} hasn’t priced a scenario yet.</span>
          )}
        </div>
      )}

      {hasHistory && (
        <>
          <div className="hist-divider">
            <span>Previous rounds · {prospect.history.length}</span>
            <span className="ln" />
          </div>
          {prospect.history.map((h) => (
            <React.Fragment key={h.round}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '2px 0 -4px' }}>
                <span className="round-tag">Round {h.round}</span>
                <span style={{ fontSize: 12, color: 'var(--lw-muted)' }}>
                  {h.scenarios.length} scenario{h.scenarios.length > 1 ? 's' : ''}
                  {h.archivedAt ? ` · reset ${new Date(h.archivedAt).toLocaleDateString()}` : ''}
                </span>
              </div>
              {h.scenarios.map((s) => (
                <ScnCard key={s.id} scn={s} hist />
              ))}
            </React.Fragment>
          ))}
        </>
      )}

      <div className="hist-divider pricing-failure-divider">
        <span>Latest rejected attempts · {failures.length} shown (max 20)</span>
        <span className="ln" />
      </div>
      {failuresLoading && <div className="pricing-failure-state">Loading rejected attempts…</div>}
      {failuresError && <div className="pricing-failure-state is-error">{failuresError}</div>}
      {!failuresLoading && !failuresError && failures.length === 0 && (
        <div className="pricing-failure-state">No rejected pricing attempts recorded.</div>
      )}
      {failures.map((failure) => <FailureCard key={failure.id} failure={failure} />)}
    </div>
  );
}

export type RowAction =
  | 'scenarios'
  | 'resend'
  | 'restore-subscription'
  | 'transition'
  | 'edit'
  | 'pass'
  | 'reopen';

export function RowMenu({
  prospect,
  onAction
}: {
  prospect: LoProspect;
  onAction: (action: RowAction, prospect: LoProspect) => void;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    const close = () => setOpen(false);
    window.addEventListener('scroll', close, true);
    return () => {
      window.removeEventListener('scroll', close, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    const closeOnWindowEscape = (event: KeyboardEvent) => {
      if (event.key !== 'Escape' || event.defaultPrevented || event.target !== event.currentTarget) {
        return;
      }
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    window.addEventListener('keydown', closeOnWindowEscape, true);
    return () => window.removeEventListener('keydown', closeOnWindowEscape, true);
  }, [open]);

  const act = (name: RowAction) => () => {
    onAction(name, prospect);
  };

  const terminal = prospect.stage === 'in_recruiting' || prospect.stage === 'passed';
  // Self-registered rows awaiting review: activation happens ONLY via the
  // invite, which approves + emails the tokenized link (raw enables are
  // refused server-side for this stage).
  const pendingReview = prospect.stage === 'registered';

  return (
    <DropdownMenuPrimitive.Root modal={false} open={open} onOpenChange={setOpen}>
      <DropdownMenuPrimitive.Trigger asChild>
        <button
          ref={triggerRef}
          className="iconbtn"
          onClick={(event) => {
            event.stopPropagation();
            if (event.detail === 0 && !open) {
              setOpen(true);
            }
          }}
          aria-label="Actions"
          type="button"
        >
          <svg width={17} height={17} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 5a2 2 0 110 4 2 2 0 010-4zm0 5a2 2 0 110 4 2 2 0 010-4zm0 5a2 2 0 110 4 2 2 0 010-4z" />
          </svg>
        </button>
      </DropdownMenuPrimitive.Trigger>
      <DropdownMenuPrimitive.Portal>
        <div className={LW_DS_CLASS}>
          <DropdownMenuPrimitive.Content
            className="menu"
            align="end"
            sideOffset={6}
            style={{ zIndex: 50 }}
            onClick={(event) => event.stopPropagation()}
            onCloseAutoFocus={(event) => {
              event.preventDefault();
              triggerRef.current?.focus();
            }}
          >
            <DropdownMenuPrimitive.Label className="menu-label">Prospect</DropdownMenuPrimitive.Label>
            {!terminal && prospect.previewInviteUnsubscribedAt && (
              <DropdownMenuPrimitive.Item asChild onSelect={act('restore-subscription')}>
                <button className="menu-item" type="button">
                  <MailCheck size={16} />
                  Restore invite access
                </button>
              </DropdownMenuPrimitive.Item>
            )}
            {!terminal && !prospect.previewInviteUnsubscribedAt && (
              <DropdownMenuPrimitive.Item asChild onSelect={act('resend')}>
                <button className="menu-item" type="button">
                  <Mail size={16} />
                  {prospect.publicPreviewEnabled ? 'Resend preview invite' : pendingReview ? 'Activate & send invite' : 'Send preview invite'}
                </button>
              </DropdownMenuPrimitive.Item>
            )}
            {prospect.runsUsed > 0 && (
              <DropdownMenuPrimitive.Item asChild onSelect={act('scenarios')}>
                <button className="menu-item" type="button">
                  <Eye size={16} />
                  View priced scenarios
                </button>
              </DropdownMenuPrimitive.Item>
            )}
            {prospect.stage === 'passed' && (
              <DropdownMenuPrimitive.Item asChild onSelect={act('reopen')}>
                <button className="menu-item" type="button">
                  <RotateCcw size={16} />
                  Reopen prospect
                </button>
              </DropdownMenuPrimitive.Item>
            )}
            {!terminal && (
              <DropdownMenuPrimitive.Item asChild onSelect={act('transition')}>
                <button className="menu-item" type="button">
                  <ArrowRight size={16} />
                  Transfer to Recruiting Table
                </button>
              </DropdownMenuPrimitive.Item>
            )}
            <DropdownMenuPrimitive.Separator className="menu-sep" />
            <DropdownMenuPrimitive.Item asChild onSelect={act('edit')}>
              <button className="menu-item" type="button">
                <Pencil size={16} />
                Edit details
              </button>
            </DropdownMenuPrimitive.Item>
            {!terminal && (
              <DropdownMenuPrimitive.Item asChild onSelect={act('pass')}>
                <button className="menu-item danger" type="button">
                  <Ban size={16} />
                  Mark as passed
                </button>
              </DropdownMenuPrimitive.Item>
            )}
          </DropdownMenuPrimitive.Content>
        </div>
      </DropdownMenuPrimitive.Portal>
    </DropdownMenuPrimitive.Root>
  );
}
